import React, { type FormEvent, useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Link as MuiLink,
  Skeleton,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useFetch } from "../../services/hooks/useFetch";
import { userApiPrefix } from "../../utils/main";
import { convertDate } from "../../utils/dateTimeUtils";
import Marketing from "./Marketing";
import { useFusionAuth, type UserInfo } from "@fusionauth/react-sdk";
import { HTTPMethod } from "@fusionauth/typescript-client";

interface BulkAgreeRequest {
  userAgreements: UserAgreement[];
}

export default function Agreements() {
  const navigate = useNavigate();
  const agreementType =
    import.meta.env.VITE_AGREEMENT_TYPE || window._env_?.AGREEMENT_TYPE;
  const [newAgreements, setNewAgreements] = useState<
    Record<string, UserAgreement>
  >({});
  const {
    data: envAgreements,
    fetch: getAgreementsForEnvironment,
    isLoading: envAgreementsLoading,
    status: envAgreementsStatus,
  } = useFetch<Agreement[]>();
  const { update: bulkAgree } = useFetch<void, BulkAgreeRequest>();
  const { refreshToken, fetchUserInfo } = useFusionAuth<UserInfo>();

  function openAgreement(url: string) {
    window.open(url);
  }

  function isAgreementAccepted(agreement: Agreement) {
    return !!agreement.acceptance || !!newAgreements[agreement.id];
  }

  function changeAgreementChecked(agreement: Agreement) {
    if (newAgreements[agreement.id]) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [agreement.id]: _, ...updatedAgreements } = newAgreements;
      setNewAgreements(updatedAgreements);
    } else {
      setNewAgreements({
        ...newAgreements,
        [agreement.id]: {
          agreementId: agreement.id,
          versionId: agreement.currentVersion.id,
        },
      });
    }
  }

  function handleAgree(event: FormEvent) {
    event.preventDefault();
    void bulkAgree({
      path: `${userApiPrefix}/user_agreement/accept/bulk`,
      body: {
        userAgreements: Object.values(newAgreements),
      },
      method: HTTPMethod.POST,
      postResponseAction: async () => {
        await refreshToken();
        await fetchUserInfo();
        navigate("/");
      },
    });
  }

  useEffect(() => {
    if (!envAgreementsLoading && envAgreementsStatus === null) {
      void getAgreementsForEnvironment(
        `${userApiPrefix}/user_agreement/${agreementType}`,
      );
    }
  }, [
    agreementType,
    envAgreementsLoading,
    envAgreementsStatus,
    getAgreementsForEnvironment,
  ]);

  return (
    <div className="signup-layout">
      <Marketing />
      <div className="authentication">
        <form onSubmit={handleAgree} className="login-form">
          <div>
            <Typography variant="h6" gutterBottom>
              You have Logged In!
            </Typography>
            <Typography variant="body2" gutterBottom>
              Before you continue, you must agree to the terms of all the
              documents below.
            </Typography>
          </div>
          {envAgreementsLoading ? (
            <Skeleton animation="wave" />
          ) : (
            <div className="terms-of-service-box">
              {envAgreements &&
                envAgreements.map((agreement) => {
                  return (
                    <div
                      className="terms-of-service"
                      key={`agreement-${agreement.id}`}
                    >
                      <Checkbox
                        id={`checkbox-${agreement.id}`}
                        onChange={() => changeAgreementChecked(agreement)}
                        checked={isAgreementAccepted(agreement)}
                        disabled={!!agreement.acceptance}
                      />
                      <Typography variant="body2">
                        Checking this box means you agree with the{" "}
                        <MuiLink
                          onClick={() =>
                            openAgreement(agreement.currentVersion.url)
                          }
                          className="terms-of-service-text"
                          variant={"subtitle2"}
                        >
                          {agreement.currentVersion.name}
                        </MuiLink>
                        {"."}
                        {!!agreement.acceptance &&
                          ` (agreed ${convertDate.stringToDate(agreement.acceptance.acceptedDate)?.toDateString()})`}
                      </Typography>
                    </div>
                  );
                })}
            </div>
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            data-testid="Agreements-submit"
            disabled={
              !envAgreements ||
              !envAgreements.every((a) => {
                return !!a.acceptance || !!newAgreements[a.id];
              })
            }
          >
            Submit
          </Button>
        </form>
      </div>
    </div>
  );
}
